import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const FMT = { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:false };
function toLocal(ts) {
  if (!ts) return '—';
  const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
  return isNaN(d) ? ts : d.toLocaleString('es-MX', FMT);
}

export default function HistorialPanel({ tipo, entidadId, entidad, mapaInst, onClose, esAdmin }) {
  const apiBase = tipo === 'flota'
    ? `/api/v1/flota/vehiculos/${entidadId}`
    : `/api/v1/objetivos/${entidadId}`;
  const nombre = tipo === 'flota'
    ? `${entidad.nombre} (${entidad.placas})`
    : `${entidad.nombre_clave || ''} — ${entidad.caso_ref || ''}`;

  const hoy = new Date().toISOString().slice(0, 10);
  const [fechaIni, setFechaIni] = useState(hoy + 'T00:00');
  const [fechaFin, setFechaFin] = useState(hoy + 'T23:59');
  const [velMin, setVelMin]     = useState('');
  const [velMax, setVelMax]     = useState('');
  const [posiciones, setPosiciones] = useState([]);
  const [stats, setStats]       = useState({});
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);
  const [cargando, setCargando] = useState(false);
  const [vista, setVista]       = useState('mapa'); // mapa | tabla
  const [msgExport, setMsgExport] = useState('');
  const routeRef = useRef(null);
  const omsRef   = useRef(null);

  const limpiarRuta = useCallback(() => {
    if (routeRef.current && mapaInst?.current) {
      mapaInst.current.removeLayer(routeRef.current);
      routeRef.current = null;
    }
  }, [mapaInst]);

  useEffect(() => { return limpiarRuta; }, [limpiarRuta]);

  const buscar = useCallback(async (pg = 1) => {
    setCargando(true);
    setPage(pg);
    try {
      const params = { page: pg, per_page: 200 };
      if (fechaIni) params.fecha_ini = fechaIni;
      if (fechaFin) params.fecha_fin = fechaFin;
      if (velMin) params.vel_min = parseFloat(velMin);
      if (velMax) params.vel_max = parseFloat(velMax);
      const { data } = await axios.get(`${apiBase}/historial`, { params });
      setPosiciones(data.posiciones || []);
      setStats(data.stats || {});
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      dibujarRuta(data.posiciones || []);
    } catch (e) { console.error(e); }
    setCargando(false);
  }, [apiBase, fechaIni, fechaFin, velMin, velMax]);

  const dibujarRuta = (pos) => {
    limpiarRuta();
    if (!mapaInst?.current || !pos.length) return;
    const L = window.L;
    const group = L.layerGroup().addTo(mapaInst.current);
    routeRef.current = group;
    omsRef.current = new window.OverlappingMarkerSpiderfier(mapaInst.current, {
      nearbyDistance: 15, circleSpiralSwitchover: 9,
      circleFootSeparation: 40, spiralFootSeparation: 28,
      spiralLengthStart: 11, spiralLengthFactor: 4,
      keepSpiderfied: true, legWeight: 1.5,
      legColors: { usual: 'rgba(255,102,0,0.4)', highlighted: '#ff6600' },
    });

    const pts = pos.filter(p => p.lat && p.lng);
    const ll = pts.map(p => [p.lat, p.lng]);
    if (ll.length > 1) L.polyline(ll, { color:'#ff6600', weight:3, opacity:.8 }).addTo(group);

    pts.forEach((p, i) => {
      const first = i === 0, last = i === pts.length - 1;
      const fill = first ? '#4ade80' : last ? '#f87171' : '#ff6600';
      const r = (first || last) ? 7 : 4;
      const d = r * 2;
      const ts = toLocal(p.timestamp);
      const popup = `<div style="min-width:170px;font-size:12px;color:#e0e1dd;background:#0b132b;padding:8px;border-radius:6px">
        <div style="font-weight:700;color:#ff6600;margin-bottom:4px">${nombre}</div>
        <div>📍 ${p.lat.toFixed(6)}, ${p.lng.toFixed(6)}</div>
        <div>🕐 ${ts}</div>
        <div>🏎️ ${p.velocidad || 0} km/h</div>
        ${first ? '<div style="color:#4ade80;margin-top:4px">📍 Inicio</div>' : ''}
        ${last ? '<div style="color:#f87171;margin-top:4px">🏁 Fin</div>' : ''}
      </div>`;
      const icon = L.divIcon({
        className: '',
        html: `<div style="width:${d}px;height:${d}px;border-radius:50%;background:${fill};
                     opacity:.9;border:2px solid rgba(255,255,255,.35);box-shadow:0 0 5px ${fill}88"></div>`,
        iconSize: [d, d], iconAnchor: [r, r], popupAnchor: [0, -r],
      });
      const m = L.marker([p.lat, p.lng], { icon }).bindPopup(popup, { maxWidth:240 });
      omsRef.current.addMarker(m);
      m.addTo(group);
    });

    if (ll.length) {
      try { mapaInst.current.fitBounds(L.latLngBounds(ll), { padding:[40,40] }); } catch(_){}
    }
  };

  const exportar = (formato) => {
    const params = new URLSearchParams();
    params.set('formato', formato);
    if (fechaIni) params.set('fecha_ini', fechaIni);
    if (fechaFin) params.set('fecha_fin', fechaFin);
    window.open(`${apiBase}/exportar?${params}`, '_blank');
  };

  const exportarCM = async () => {
    setMsgExport('Exportando…');
    try {
      const { data } = await axios.post(`${apiBase}/exportar-cm`, {
        fecha_ini: fechaIni, fecha_fin: fechaFin
      });
      setMsgExport(data.ok ? `✅ Caso #${data.caso_id} creado` : 'Error al crear caso');
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (detail.includes('404') || detail.includes('Not Found'))
        setMsgExport('❌ Case Manager no disponible — verificar deploy');
      else
        setMsgExport(`❌ ${detail || 'Error de conexión'}`);
    }
    setTimeout(() => setMsgExport(''), 8000);
  };

  const S = {
    panel: { display:'flex', flexDirection:'column', height:'100%', background:'#0b132b' },
    header: { padding:'10px 14px', borderBottom:'1px solid #1c2541', display:'flex', alignItems:'center', gap:8 },
    title: { fontWeight:700, color:'#ff6600', fontSize:13, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
    close: { background:'none', border:'1px solid #1c2541', color:'#8892a4', borderRadius:6, padding:'3px 8px', cursor:'pointer', fontSize:12 },
    filters: { padding:'8px 14px', borderBottom:'1px solid #1c2541', display:'flex', flexDirection:'column', gap:6 },
    row: { display:'flex', gap:6, alignItems:'center' },
    inp: { flex:1, background:'#04080f', border:'1px solid #1c2541', color:'#e0e1dd', borderRadius:5, padding:'5px 8px', fontSize:12, colorScheme:'dark' },
    btn: { background:'#ff6600', border:'none', color:'#fff', borderRadius:6, padding:'5px 12px', cursor:'pointer', fontSize:11, fontWeight:700 },
    btnSm: { background:'#1c2541', border:'none', color:'#8892a4', borderRadius:5, padding:'3px 8px', cursor:'pointer', fontSize:10 },
    btnAct: { background:'#ff6600', border:'none', color:'#fff', borderRadius:5, padding:'3px 8px', cursor:'pointer', fontSize:10, fontWeight:700 },
    stats: { padding:'8px 14px', borderBottom:'1px solid #1c2541', fontSize:11, color:'#8892a4', display:'flex', flexWrap:'wrap', gap:8 },
    statVal: { color:'#e0e1dd', fontWeight:700 },
    body: { flex:1, overflow:'auto', padding:8 },
    tr: { display:'flex', padding:'4px 8px', borderBottom:'1px solid #0b132b', fontSize:10, gap:4, alignItems:'center' },
    trHead: { display:'flex', padding:'6px 8px', background:'#04080f', borderBottom:'1px solid #1c2541', fontSize:10, gap:4, fontWeight:700, color:'#8892a4', position:'sticky', top:0 },
    td: { flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'#e0e1dd' },
    tdN: { width:80, textAlign:'right', color:'#e0e1dd' },
    pag: { padding:'6px 14px', borderTop:'1px solid #1c2541', display:'flex', justifyContent:'center', gap:6, alignItems:'center', fontSize:11, color:'#8892a4' },
    expBar: { padding:'6px 14px', borderTop:'1px solid #1c2541', display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' },
  };

  return (
    <div style={S.panel}>
      <div style={S.header}>
        <span style={S.title}>📊 {nombre}</span>
        <button style={S.close} onClick={() => { limpiarRuta(); onClose(); }}>✕ Cerrar</button>
      </div>

      {/* Filtros */}
      <div style={S.filters}>
        <div style={S.row}>
          <span style={{ color:'#8892a4', fontSize:10, width:36 }}>Desde</span>
          <input type="datetime-local" value={fechaIni} onChange={e => setFechaIni(e.target.value)} style={S.inp} />
        </div>
        <div style={S.row}>
          <span style={{ color:'#8892a4', fontSize:10, width:36 }}>Hasta</span>
          <input type="datetime-local" value={fechaFin} onChange={e => setFechaFin(e.target.value)} style={S.inp} />
        </div>
        <div style={S.row}>
          <input placeholder="Vel mín" value={velMin} onChange={e => setVelMin(e.target.value)}
                 style={{...S.inp, width:70, flex:'none'}} />
          <input placeholder="Vel máx" value={velMax} onChange={e => setVelMax(e.target.value)}
                 style={{...S.inp, width:70, flex:'none'}} />
          <span style={{ flex:1 }} />
          <button style={S.btn} onClick={() => buscar(1)} disabled={cargando}>
            {cargando ? '…' : '🔍 Buscar'}
          </button>
        </div>
      </div>

      {/* Stats */}
      {total > 0 && (
        <div style={S.stats}>
          <span>Total: <b style={S.statVal}>{total}</b></span>
          <span>Vel prom: <b style={S.statVal}>{stats.vel_promedio || 0}</b> km/h</span>
          <span>Vel máx: <b style={S.statVal}>{stats.vel_maxima || 0}</b> km/h</span>
          {stats.bateria_avg && <span>Bat: <b style={S.statVal}>{stats.bateria_avg}%</b></span>}
        </div>
      )}

      {/* Vista toggle */}
      {total > 0 && (
        <div style={{ padding:'4px 14px', display:'flex', gap:4 }}>
          <button style={vista === 'mapa' ? S.btnAct : S.btnSm} onClick={() => setVista('mapa')}>🗺️ Mapa</button>
          <button style={vista === 'tabla' ? S.btnAct : S.btnSm} onClick={() => setVista('tabla')}>📋 Tabla</button>
        </div>
      )}

      {/* Tabla */}
      {vista === 'tabla' && total > 0 && (
        <div style={S.body}>
          <div style={S.trHead}>
            <div style={{...S.td, flex:2}}>Fecha/Hora</div>
            <div style={S.tdN}>Lat</div>
            <div style={S.tdN}>Lng</div>
            <div style={{...S.tdN, width:50}}>Vel</div>
            <div style={{...S.tdN, width:40}}>Bat</div>
          </div>
          {posiciones.map((p, i) => {
            const ts = toLocal(p.timestamp);
            return (
              <div key={p.id || i} style={{...S.tr, background: i%2 ? '#04080f' : 'transparent', cursor:'pointer'}}
                onClick={() => {
                  if (p.lat && p.lng && mapaInst?.current) {
                    mapaInst.current.setView([p.lat, p.lng], 16);
                    setVista('mapa');
                  }
                }}>
                <div style={{...S.td, flex:2}}>{ts}</div>
                <div style={S.tdN}>{p.lat?.toFixed(5)}</div>
                <div style={S.tdN}>{p.lng?.toFixed(5)}</div>
                <div style={{...S.tdN, width:50}}>{p.velocidad || 0}</div>
                <div style={{...S.tdN, width:40}}>{p.bateria_pct ?? '—'}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mapa placeholder (el mapa real es del padre) */}
      {vista === 'mapa' && total > 0 && (
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'#1c2541', fontSize:12 }}>
          Ruta dibujada en el mapa · {posiciones.filter(p=>p.lat&&p.lng).length} puntos
        </div>
      )}

      {total === 0 && !cargando && (
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'#8892a4', fontSize:12 }}>
          Selecciona fechas y presiona Buscar
        </div>
      )}

      {/* Paginación */}
      {pages > 1 && (
        <div style={S.pag}>
          <button style={S.btnSm} disabled={page <= 1} onClick={() => buscar(page-1)}>◀</button>
          <span>Pág {page}/{pages}</span>
          <button style={S.btnSm} disabled={page >= pages} onClick={() => buscar(page+1)}>▶</button>
        </div>
      )}

      {/* Exportar */}
      {total > 0 && (
        <div style={S.expBar}>
          <button style={S.btnSm} onClick={() => exportar('csv')}>📥 CSV</button>
          <button style={S.btnSm} onClick={() => exportar('json')}>📥 JSON</button>
          {esAdmin && <button style={{...S.btnSm, background:'#1c3828', color:'#4ade80'}} onClick={exportarCM}>📤 Case Manager</button>}
          {msgExport && <span style={{ fontSize:11, color:'#4ade80' }}>{msgExport}</span>}
        </div>
      )}
    </div>
  );
}
